/**
 * Utilitários de formatação para números e valores
 *
 * Padrão brasileiro (pt-BR):
 * - Separador de milhar: ponto (.)
 * - Separador decimal: vírgula (,)
 */

/**
 * Formata quantidade para exibição com padrão brasileiro
 *
 * Comportamento:
 * - Se valor é inteiro (sem decimais significativos): formata com separador de milhar
 * - Se valor tem decimais: formata com vírgula decimal
 *
 * @param valor - Número a ser formatado (pode ser null)
 * @param casasDecimais - Número de casas decimais para valores não-inteiros (padrão: 3)
 * @returns String formatada no padrão brasileiro
 *
 * @example
 * formatarQuantidade(250.000) // "250"
 * formatarQuantidade(1500.000) // "1.500"
 * formatarQuantidade(123.218) // "123,218"
 * formatarQuantidade(1234567.890) // "1.234.567,890"
 * formatarQuantidade(null) // "-"
 */
export function formatarQuantidade(
  valor: number | null | undefined,
  casasDecimais: number = 3
): string {
  // Tratar valores nulos/indefinidos
  if (valor === null || valor === undefined) {
    return '-';
  }

  // Detectar se é inteiro (diferença menor que 0.001)
  const isInteger = Math.abs(valor - Math.round(valor)) < 0.001;

  if (isInteger) {
    // Formato inteiro com separador de milhar: 1.500
    return Math.round(valor).toLocaleString('pt-BR');
  } else {
    // Formato decimal com vírgula: 123,218
    return valor.toLocaleString('pt-BR', {
      minimumFractionDigits: casasDecimais,
      maximumFractionDigits: casasDecimais
    });
  }
}

/**
 * Formata diferença de quantidade com sinal (+/-)
 *
 * @param valor - Número a ser formatado
 * @returns String formatada com sinal
 *
 * @example
 * formatarDiferenca(68.000) // "+68"
 * formatarDiferenca(-1467.000) // "-1.467"
 * formatarDiferenca(0) // "0"
 */
export function formatarDiferenca(valor: number | null | undefined): string {
  if (valor === null || valor === undefined) {
    return '-';
  }

  const formatted = formatarQuantidade(valor);

  if (valor > 0) {
    return `+${formatted}`;
  }

  return formatted;
}

/**
 * Formata número como moeda brasileira (R$)
 *
 * @param valor - Valor monetário
 * @returns String formatada como moeda
 *
 * @example
 * formatarMoeda(1500.50) // "R$ 1.500,50"
 * formatarMoeda(null) // "R$ 0,00"
 */
export function formatarMoeda(valor: number | null | undefined): string {
  const valorSeguro = valor ?? 0;

  return valorSeguro.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
}

/**
 * Formata porcentagem
 *
 * @param valor - Valor decimal (ex: 0.75 para 75%)
 * @param casasDecimais - Casas decimais (padrão: 2)
 * @returns String formatada como porcentagem
 *
 * @example
 * formatarPorcentagem(0.7532) // "75,32%"
 * formatarPorcentagem(1.5) // "150,00%"
 */
export function formatarPorcentagem(
  valor: number | null | undefined,
  casasDecimais: number = 2
): string {
  if (valor === null || valor === undefined) {
    return '-';
  }

  return (valor * 100).toLocaleString('pt-BR', {
    minimumFractionDigits: casasDecimais,
    maximumFractionDigits: casasDecimais
  }) + '%';
}

/**
 * Formata data de YYYY-MM-DD para DD/MM/YYYY sem usar Date object
 * para evitar problemas de timezone.
 *
 * IMPORTANTE: Nunca use `new Date(dateString).toLocaleDateString('pt-BR')`
 * para strings de data-only (YYYY-MM-DD). O JavaScript interpreta como UTC
 * meia-noite, e no Brasil (UTC-3) a data é deslocada para trás.
 *
 * @param dateString - Data no formato YYYY-MM-DD, DD/MM/YYYY, ou null/undefined
 * @returns String formatada como DD/MM/YYYY ou string vazia
 *
 * @example
 * formatDateBR("2026-01-20") // "20/01/2026"
 * formatDateBR("20/01/2026") // "20/01/2026" (já formatado)
 * formatDateBR(null) // ""
 * formatDateBR(undefined) // ""
 */
export function formatDateBR(dateString: string | null | undefined): string {
  if (!dateString) return '';

  // Já no formato DD/MM/YYYY
  if (dateString.includes('/')) return dateString;

  // Converter de YYYY-MM-DD para DD/MM/YYYY
  if (dateString.includes('-')) {
    // Handle datetime strings like "2026-01-20T14:30:00"
    const datePart = dateString.split('T')[0];
    const parts = datePart.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
  }

  return dateString;
}
